(() => {
  'use strict';
  if (window.NNFounderVideos?.version === 2) {
    window.NNFounderVideos.init(document);
    return;
  }
  const instances = new WeakMap();
  const records = new Set();
  let active;
  let ownedScrollLock = false;

  function lockScroll() {
    if (!document.body.classList.contains('nnfv-scroll-lock')) {
      ownedScrollLock = true;
      document.body.classList.add('nnfv-scroll-lock');
    }
  }

  function unlockScroll() {
    if (!ownedScrollLock) return;
    document.body.classList.remove('nnfv-scroll-lock');
    ownedScrollLock = false;
  }

  function capture(name, video) {
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return false;
      if (typeof window.NNPostHog?.capture !== 'function') return false;
      window.NNPostHog.capture(name, {
        video_key: video.closest('[data-nnfv-dialog]')?.dataset.videoKey || '',
        page_path: window.location.pathname
      });
      return true;
    } catch (error) { return false; }
  }

  function pauseOthers(video) {
    document.querySelectorAll('video').forEach(other => {
      if (other !== video && !other.paused) other.pause();
    });
  }

  function close(record, restore = true) {
    record.restoreFocus = restore;
    record.video.pause();
    if (record.dialog.open) record.dialog.close();
  }

  function open(record, opener) {
    if (active && active !== record) close(active, false);
    record.opener = opener;
    record.restoreFocus = true;
    pauseOthers(record.video);
    try {
      if (!record.dialog.open) record.dialog.showModal();
    } catch (error) { return false; }
    active = record;
    lockScroll();
    record.dialog.querySelector('[data-nnfv-close]')?.focus({ preventScroll: true });
    if (record.video.error || record.video.networkState === 3) record.video.load();
    record.video.play().catch(() => {});
    return true;
  }

  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const localRecords = [];
    const byId = new Map();
    root.querySelectorAll('[data-nnfv-dialog]').forEach(dialog => {
      const video = dialog.querySelector('[data-nnfv-video]');
      if (!video || typeof dialog.showModal !== 'function') return;
      const record = { dialog, video, opener: null, restoreFocus: true };
      let started = false;
      let completed = false;
      let failedSources = new WeakSet();
      let failedSourceCount = 0;
      const sources = [...video.querySelectorAll('source')];
      const message = dialog.querySelector('[data-nnfv-error]');
      const showUnavailable = () => { if (message) message.hidden = false; };
      video.addEventListener('loadstart', () => {
        failedSources = new WeakSet();
        failedSourceCount = 0;
        if (message) message.hidden = true;
      }, { signal });
      sources.forEach(source => {
        source.addEventListener('error', () => {
          if (failedSources.has(source)) return;
          failedSources.add(source);
          failedSourceCount += 1;
          if (failedSourceCount === sources.length) showUnavailable();
        }, { signal });
      });
      video.addEventListener('loadeddata', () => { if (message) message.hidden = true; }, { signal });
      video.addEventListener('error', showUnavailable, { signal });
      video.querySelector('[data-nnfv-captions]')?.addEventListener('error', () => {
        const captionMessage = dialog.querySelector('[data-nnfv-caption-error]');
        if (captionMessage) captionMessage.hidden = false;
      }, { signal });
      video.addEventListener('play', () => {
        pauseOthers(video);
        if (!started && capture('video_play', video)) started = true;
      }, { signal });
      video.addEventListener('ended', () => {
        if (!completed && capture('video_complete', video)) completed = true;
      }, { signal });
      dialog.querySelector('[data-nnfv-close]')?.addEventListener('click', () => close(record), { signal });
      dialog.addEventListener('cancel', event => {
        event.preventDefault();
        close(record);
      }, { signal });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close(record);
      }, { signal });
      dialog.addEventListener('close', () => {
        video.pause();
        if (active === record) {
          active = undefined;
          unlockScroll();
        }
        if (record.restoreFocus && record.opener?.isConnected) record.opener.focus({ preventScroll: true });
        record.opener = null;
        dialog.querySelector('[data-nnfv-transcript]')?.removeAttribute('open');
      }, { signal });
      byId.set(dialog.id, record);
      localRecords.push(record);
      records.add(record);
    });
    root.querySelectorAll('[data-nnfv-open]').forEach(link => {
      const record = byId.get(link.dataset.nnfvOpen);
      if (!record) return;
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', record.dialog.id);
      link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (open(record, link)) event.preventDefault();
      }, { signal });
    });
    instances.set(root, () => {
      localRecords.forEach(record => {
        close(record, false);
        records.delete(record);
      });
      if (localRecords.includes(active)) {
        active = undefined;
        unlockScroll();
      }
      controller.abort();
      instances.delete(root);
    });
  }

  function initAll(scope) {
    if (scope.matches?.('[data-nnfv-gallery]')) init(scope);
    scope.querySelectorAll('[data-nnfv-gallery]').forEach(init);
  }

  const pauseAll = () => records.forEach(record => record.video.pause());
  window.NNFounderVideos = { init: initAll, version: 2 };
  initAll(document);
  document.addEventListener('visibilitychange', () => { if (document.hidden) pauseAll(); });
  window.addEventListener('pagehide', pauseAll);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-nnfv-gallery]')) instances.get(event.target)?.();
    event.target.querySelectorAll('[data-nnfv-gallery]').forEach(root => instances.get(root)?.());
  });
})();
